/**
 * RequestModal, universal "Make a request" modal.
 *
 * Per Ean's 2026-06-03 design pass + 2026-06-10 consolidation:
 *
 *   - Modal takes a `destination` prop ('admin' | 'olytic'). Caller decides
 *     which inbox the request routes to; modal renders accordingly.
 *
 *   - destination='admin' (title: "Contact Your Admin"):
 *       everyone → "Request access", "Ask a question"
 *     Two options. Same pair for admins too — an admin clicking through
 *     this surface is rare but the modal still works (lands in their own
 *     inbox).
 *
 *   - destination='olytic' (title: "Contact Olytic"):
 *       everyone → "Request or suggest something" (catch-all for feature
 *                  ideas, new vendors, polish), "Contact Olytic
 *                  Professional Services" (bespoke / paid engagement)
 *       admin/owner only → "Contact Olytic Support" (billing, urgent,
 *                          account questions)
 *     Three options total. Consolidated from a previous 5 — feedback +
 *     new_connector kinds dropped from visible options (still in the
 *     RequestType union for backward compat with old rows). Support is
 *     gated because account-level questions typically need authorization.
 *
 *   - Submit button label changes based on destination.
 *
 * Submits to POST /api/me/requests which writes to sys__user_event_log per
 * D-096.
 */

import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';

import { ApiError, createRequest, type RequestType } from '../lib/api.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useToast } from './Toast.tsx';
import { LoadingButton } from './LoadingButton.tsx';

export type RequestDestination = 'admin' | 'olytic';

interface RequestModalProps {
  open: boolean;
  onClose: () => void;
  /** Where the request goes. Caller decides; modal renders for it. */
  destination: RequestDestination;
  /** Optional initial picklist value. */
  initialKind?: RequestType;
  /** Optional pre-fill for Subject. */
  subjectHint?: string;
  /** Optional template_name to attach (for connector access requests). */
  initialTemplateName?: string;
}

interface KindOption {
  value: RequestType;
  label: string;
  helper: string;
  /** Visible only to admins/owners when true. */
  adminOnly?: boolean;
}

/**
 * Kind picklists, per destination. Admin-only options are filtered out for
 * standard users and rendered in a default-collapsed expander for admins.
 */
function kindsForDestination(destination: RequestDestination): {
  primary: KindOption[];
  adminOnly: KindOption[];
} {
  if (destination === 'olytic') {
    // Consolidated to 3 options per Ean 2026-06-10. Previous shape had
    // feedback + suggestion + new_connector + custom_connector + support
    // (5 options) which was too many for the volume of intent we actually
    // see. New shape: one general "request/suggest" catch-all, one
    // Professional Services entry, one gated Support entry.
    return {
      primary: [
        {
          value: 'suggestion',
          label: 'Request or suggest something',
          helper: 'Feature ideas, vendors you wish we had, polish, anything.',
        },
        {
          value: 'custom_connector',
          label: 'Contact Olytic Professional Services',
          helper: 'Bespoke integrations, custom workflows, paid engagements.',
        },
      ],
      adminOnly: [
        {
          value: 'support',
          label: 'Contact Olytic Support',
          helper: 'Billing, urgent issues, account questions.',
          adminOnly: true,
        },
      ],
    };
  }

  // destination === 'admin'
  return {
    primary: [
      {
        value: 'connector_access',
        label: 'Request access',
        helper: 'A model, connector, prompt, or anything else your admin can grant.',
      },
      {
        value: 'support',
        label: 'Ask a question',
        helper: 'General question, training, or workspace issue.',
      },
    ],
    adminOnly: [],
  };
}

function destinationCopy(destination: RequestDestination): {
  title: string;
  helper: string;
  submitLabel: string;
} {
  if (destination === 'olytic') {
    return {
      title: 'Contact Olytic',
      helper: 'We respond within 48 hours.',
      submitLabel: 'Send to Olytic',
    };
  }
  return {
    // Per Ean 2026-06-10 (revised): match the "Contact Olytic" voice —
    // single-noun title, no laundry list of what the modal can do. Kind
    // picker below disambiguates request-access vs ask-a-question.
    title: 'Contact your admin',
    helper: '',
    submitLabel: 'Send to admin',
  };
}

export function RequestModal({
  open,
  onClose,
  destination,
  initialKind,
  subjectHint,
  initialTemplateName,
}: RequestModalProps) {
  const { showToast } = useToast();
  const { user } = useSession();
  const isAdmin = user?.role === 'admin' || user?.role === 'owner';
  const kinds = kindsForDestination(destination);
  const visibleAdminOnly = isAdmin ? kinds.adminOnly : [];
  const allOptions = [...kinds.primary, ...visibleAdminOnly];

  const initial: RequestType = (() => {
    if (initialKind !== undefined && allOptions.some((k) => k.value === initialKind)) {
      return initialKind;
    }
    return kinds.primary[0]?.value ?? 'feedback';
  })();

  const [kind, setKind] = useState<RequestType>(initial);
  const [subject, setSubject] = useState(subjectHint ?? '');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      const next =
        initialKind !== undefined && allOptions.some((k) => k.value === initialKind)
          ? initialKind
          : kinds.primary[0]?.value ?? 'feedback';
      setKind(next);
      setSubject(subjectHint ?? '');
      setBody('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialKind, subjectHint, destination]);

  // Phase 2 Finishing Pass R2 Item 12 — ESC closes the modal. Pattern matches
  // ConfirmDialog + BuyCreditsModal; previously this modal had no escape
  // handler so a user mid-request had to find the X to bail. Skipped while
  // submitting (the network call should complete or fail; closing mid-fly
  // would orphan the request).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && !submitting) {
        e.preventDefault();
        handleClose();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // handleClose is stable for the modal lifetime (depends on onClose prop
    // which the parent owns); intentionally omitted from deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, submitting]);

  if (!open) return null;

  const copy = destinationCopy(destination);

  function reset() {
    setKind(initial);
    setSubject(subjectHint ?? '');
    setBody('');
  }

  function handleClose() {
    reset();
    onClose();
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (subject.trim() === '' && body.trim() === '') {
      showToast({ kind: 'error', message: 'Add a subject or some details before sending.' });
      return;
    }
    setSubmitting(true);
    try {
      await createRequest({
        request_type: kind,
        subject,
        body,
        destination,
        ...(initialTemplateName !== undefined && initialTemplateName !== ''
          ? { template_name: initialTemplateName }
          : {}),
      });
      showToast({
        kind: 'success',
        message:
          destination === 'olytic'
            ? 'Sent to Olytic. We read every one.'
            : 'Sent to your admin. You can track it on your profile.',
      });
      reset();
      onClose();
    } catch (err) {
      showToast({
        kind: 'error',
        message:
          err instanceof ApiError ? err.message : 'Could not send. Try again in a moment.',
      });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label={copy.title}>
      <div className="modal-card">
        <header className="modal-header">
          <h2 className="modal-title">{copy.title}</h2>
          <button
            type="button"
            className="modal-close"
            onClick={handleClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <form className="modal-body" onSubmit={handleSubmit}>
          {copy.helper !== '' && (
            <p className="muted" style={{ fontSize: '13px', margin: '0 0 14px' }}>
              {copy.helper}
            </p>
          )}

          <div className="form-field">
            <label className="form-label">What kind?</label>
            <div className="request-type-grid">
              {kinds.primary.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`request-type-card${kind === opt.value ? ' active' : ''}`}
                  onClick={() => setKind(opt.value)}
                >
                  <span className="request-type-label">{opt.label}</span>
                  <span className="request-type-helper">{opt.helper}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Admin-only kinds inline as more cards in the same grid pattern.
             Role gate: `visibleAdminOnly` is empty for standard users, so the
             whole block doesn't render for them. For admins it sits flush
             with the primary kinds. same card style, sentence-case label,
             no separate Collapsible chrome (which read as a different page
             section). */}
          {visibleAdminOnly.length > 0 && (
            <div className="form-field">
              <label className="form-label">Admins only</label>
              <div className="request-type-grid">
                {visibleAdminOnly.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`request-type-card${kind === opt.value ? ' active' : ''}`}
                    onClick={() => setKind(opt.value)}
                  >
                    <span className="request-type-label">{opt.label}</span>
                    <span className="request-type-helper">{opt.helper}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="form-field">
            <label htmlFor="req-subject" className="form-label">
              Subject
            </label>
            <input
              id="req-subject"
              type="text"
              className="form-input"
              value={subject}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setSubject(e.target.value)}
              placeholder={subjectPlaceholderFor(kind)}
              maxLength={200}
            />
          </div>

          <div className="form-field">
            <label htmlFor="req-body" className="form-label">
              Details
            </label>
            <textarea
              id="req-body"
              className="form-input form-textarea"
              rows={5}
              value={body}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setBody(e.target.value)}
              placeholder="What would you like us to know?"
            />
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <LoadingButton
              type="submit"
              className="btn btn-primary"
              loading={submitting}
              loadingLabel="Sending..."
            >
              {copy.submitLabel}
            </LoadingButton>
          </div>
        </form>
      </div>
    </div>
  );
}

function subjectPlaceholderFor(kind: RequestType): string {
  switch (kind) {
    case 'new_connector':
      return 'Add Salesforce as a connector';
    case 'custom_connector':
      return 'Custom HubSpot integration for our region split';
    case 'suggestion':
      return 'Bulk-assign connectors to a team';
    case 'feedback':
      return 'What is great or what hurts';
    case 'support':
      return 'Short summary of what you need';
    case 'connector_access':
      return 'What you need access to';
    default:
      return 'Quick note';
  }
}
